import { ArrowRight, ArrowDown, Download } from "lucide-react";
import { toast } from "sonner";
import { openCalendly } from "@/lib/calendly";
import { downloadSampleScorecardPDF } from "@/lib/scorecardPdf";

export default function Hero() {
  const handleDownload = () => {
    try {
      downloadSampleScorecardPDF();
      toast.success("Sample scorecard PDF downloaded", {
        description: "Skim it — then book a slot when you're ready.",
      });
    } catch (e) {
      toast.error("Could not generate PDF", { description: String(e?.message || e) });
    }
  };
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-[#FAFAFA] border-b border-zinc-200"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 pt-16 sm:pt-24 lg:pt-28 pb-20 lg:pb-28">
        {/* Mono eyebrow */}
        <div className="flex items-center gap-3 reveal">
          <span className="inline-block h-px w-10 bg-orange-600" />
          <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">
            AI Bottleneck Assessment · For Small Businesses
          </span>
        </div>

        <h1
          className="mt-6 font-display font-bold text-zinc-950 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-5xl reveal"
          data-testid="hero-headline"
        >
          Find the 3 biggest{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-orange-600">bottlenecks</span>
            <span
              className="absolute left-0 right-0 bottom-1 h-3 bg-orange-600/15 z-0"
              aria-hidden="true"
            />
          </span>{" "}
          slowing your business down.
        </h1>

        <p
          className="mt-8 max-w-2xl text-lg sm:text-xl text-zinc-600 leading-relaxed reveal"
          data-testid="hero-subheadline"
        >
          We assess where your business is losing time, leads and revenue — then show exactly
          how AI and automation can fix it. No hype. No bloated software. Just the three
          changes that matter most.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 reveal">
          <a
            href="#book"
            onClick={(e) => {
              e.preventDefault();
              openCalendly();
            }}
            className="group inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-6 py-4 text-base font-medium border border-zinc-950 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(234,88,12,1)] transition-all duration-200"
            data-testid="hero-primary-cta"
          >
            Book Your Assessment
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="#assessment"
            className="group inline-flex items-center justify-center gap-2 bg-white text-zinc-950 px-6 py-4 text-base font-medium border border-zinc-300 hover:border-zinc-950 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] transition-all duration-200"
            data-testid="hero-secondary-cta"
          >
            See What We Assess
            <ArrowDown size={16} className="transition-transform group-hover:translate-y-0.5" />
          </a>
        </div>

        {/* Tertiary link: low-friction lead-magnet capture */}
        <button
          type="button"
          onClick={handleDownload}
          className="mt-5 group inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-950 transition-colors reveal"
          data-testid="hero-scorecard-download"
        >
          <Download size={14} className="text-orange-600" />
          <span>
            Prefer to read first?{" "}
            <span className="underline underline-offset-4 decoration-zinc-300 group-hover:decoration-orange-600 font-medium text-zinc-950">
              Download the Sample Scorecard (PDF)
            </span>
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 group-hover:text-orange-600">
            · no email required
          </span>
        </button>

        {/* Key stats strip */}
        <div className="mt-16 lg:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 reveal">
          {[
            { k: "3", label: "Priority bottlenecks identified" },
            { k: "~45 min", label: "Assessment call" },
            { k: "0", label: "Buzzwords, templates or fluff" },
            { k: "1-2 weeks", label: "Scorecard + roadmap delivered" },
          ].map((s, i) => (
            <div key={i} className="bg-white p-6 sm:p-8" data-testid={`hero-stat-${i}`}>
              <div className="font-display font-bold text-3xl sm:text-4xl text-zinc-950 tracking-tight">
                {s.k}
              </div>
              <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
